import * as facemesh from "@tensorflow-models/facemesh";
import * as tf from "@tensorflow/tfjs-core";
import * as tfjsWasm from "@tensorflow/tfjs-backend-wasm";
import { version } from "@tensorflow/tfjs-backend-wasm/dist/version";

import { TRIANGULATION } from "./triangulation";

tfjsWasm.setWasmPath(
  `https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-wasm@${version}/dist/tfjs-backend-wasm.wasm`
);

function isMobile() {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  return isAndroid || isiOS;
}

function drawPath(ctx, points, closePath) {
  const region = new Path2D();
  region.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    region.lineTo(point[0], point[1]);
  }

  if (closePath) {
    region.closePath();
  }
  ctx.stroke(region);
}

let model, ctx, videoWidth, videoHeight, video, canvas;

const VIDEO_SIZE_WIDTH = window.innerWidth;
const VIDEO_SIZE_HEIGHT = window.innerHeight;
const mobile = isMobile();

const state = {
  backend: "wasm",
  maxFaces: 1,
  triangulateMesh: true,
};

const addAllValues = (values) => {
  let sum = 0;

  values.forEach((value) => {
    sum += value[1];
  });
  return sum / values.length;
};

async function setupCamera() {
  video = document.getElementById("video");

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: "user",
      // Only setting the video to a specified size in order to accommodate a
      // point cloud, so on mobile devices accept the default size.
      width: mobile ? undefined : VIDEO_SIZE_WIDTH,
      height: mobile ? undefined : VIDEO_SIZE_HEIGHT,
    },
  });
  video.srcObject = stream;

  return new Promise((resolve) => {
    video.onloadedmetadata = () => {
      resolve(video);
    };
  });
}
let zoom = 1.0;

const calculateDiff = (a, b) => {
  if (a > b) {
    return a - b;
  }

  if (b > a) {
    return b - a;
  }
};

let faceRotatedRight = false;
let faceRotatedLeft = false;

async function renderPrediction() {
  const predictions = await model.estimateFaces(video);
  // const predictions = await model.estimateFaces(canvas);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.scale(zoom, zoom);

  // ctx.drawImage(
  //   video,
  //   0,
  //   0,
  //   videoWidth,
  //   videoHeight,
  //   0,
  //   0,
  //   canvas.width,
  //   canvas.height
  // );
  ctx.drawImage(video, -canvas.width / 2, -canvas.height / 2);
  ctx.restore();

  if (predictions.length > 0) {
    predictions.forEach((prediction) => {
      const keypoints = prediction.scaledMesh;

      const lipLowerInnerY = predictions[0].annotations.lipsLowerInner[5][1];
      const lipUpperInnerY = predictions[0].annotations.lipsUpperInner[5][1];

      const averageYAxisLeftEyebrowLower = addAllValues(
        predictions[0].annotations.leftEyebrowLower
      );

      const averageYAxisLeftEyebrowUpper = addAllValues(
        predictions[0].annotations.leftEyebrowUpper
      );

      const averageYAxisLeftEyebrow =
        (averageYAxisLeftEyebrowLower + averageYAxisLeftEyebrowUpper) / 2;

      const averageYAxisRightEyebrowLower = addAllValues(
        predictions[0].annotations.rightEyebrowLower
      );

      const averageYAxisRightEyebrowUpper = addAllValues(
        predictions[0].annotations.rightEyebrowUpper
      );

      const averageYAxisRightEyebrow =
        (averageYAxisRightEyebrowLower + averageYAxisRightEyebrowUpper) / 2;

      const eyebrowYDiff = averageYAxisRightEyebrow - averageYAxisLeftEyebrow;
      const eyebrowYRightDiff =
        averageYAxisLeftEyebrow - averageYAxisRightEyebrow;

      const leftEyebrowUpper = predictions[0].annotations.leftEyebrowUpper;
      const rightEyebrowUpper = predictions[0].annotations.rightEyebrowUpper;

      const outerLeftEyebrow = window.innerWidth - leftEyebrowUpper[0][0];
      const innerLeftEyebrow = window.innerWidth - leftEyebrowUpper[7][0];

      const widthLeftEyebrow = innerLeftEyebrow - outerLeftEyebrow;

      const innerRightEyebrow = window.innerWidth - rightEyebrowUpper[7][0];
      const outerRightEyebrow = window.innerWidth - rightEyebrowUpper[0][0];

      const widthRightEyebrow = outerRightEyebrow - innerRightEyebrow;

      const diffWidthEyebrows = calculateDiff(
        widthLeftEyebrow,
        widthRightEyebrow
      );

      // calculate width bounding box relative to width of window.
      const bottomRightFaceX =
        window.innerWidth - predictions[0].boundingBox.bottomRight[0][0];
      const topLeftFaceX =
        window.innerWidth - predictions[0].boundingBox.topLeft[0][0];

      const widthFace = Math.abs(bottomRightFaceX - topLeftFaceX);

      const ratioFaceScreen = window.innerWidth / widthFace;

      // face straight
      if (diffWidthEyebrows < 10) {
        if (eyebrowYDiff > 34 / ratioFaceScreen) {
          // zoom in with left eyebrow
          if (zoom < 3.5) {
            zoom *= 1.1;
          }
        }

        if (eyebrowYRightDiff > 0.8 / ratioFaceScreen) {
          // zoom out with right eyebrow
          if (zoom > 1) {
            zoom /= 1.1;
          }
        }
        faceRotatedRight = false;
        faceRotatedLeft = false;
      } else {
        if (widthRightEyebrow < widthLeftEyebrow) {
          faceRotatedRight = true;
          faceRotatedLeft = false;
        } else {
          faceRotatedLeft = true;
          faceRotatedRight = false;
        }
      }

      // Mouth open
      if (
        (lipLowerInnerY - lipUpperInnerY > 40 / ratioFaceScreen &&
          faceRotatedRight) ||
        (lipLowerInnerY - lipUpperInnerY > 40 / ratioFaceScreen &&
          faceRotatedLeft)
      ) {
        const img = document.getElementsByTagName("img")[0];
        img.style.opacity = 1;
        img.style.top = predictions[0].boundingBox.topLeft[0][1];
        img.style.left = predictions[0].boundingBox.topLeft[0][0] + 50;

        img.style.width = (widthFace * 1.2) / ratioFaceScreen;
        img.style.width =
          predictions[0].boundingBox.bottomRight[0][0] -
          predictions[0].boundingBox.topLeft[0][0] / 1.5;

        if (faceRotatedRight) {
          img.style.transform = `scaleX(-1)`;
        } else {
          img.style.transform = `scaleX(1)`;
        }
      } else {
        const img = document.getElementsByTagName("img")[0];
        img.style.opacity = 0;
      }

      // if (state.triangulateMesh) {
      //   for (let i = 0; i < TRIANGULATION.length / 3; i++) {
      //     const points = [
      //       TRIANGULATION[i * 3],
      //       TRIANGULATION[i * 3 + 1],
      //       TRIANGULATION[i * 3 + 2],
      //     ].map((index) => keypoints[index]);

      //     drawPath(ctx, points, true);
      //   }
      // } else {
      //   for (let i = 0; i < keypoints.length; i++) {
      //     const x = keypoints[i][0];
      //     const y = keypoints[i][1];

      //     ctx.beginPath();
      //     ctx.arc(x, y, 1 /* radius */, 0, 2 * Math.PI);
      //     ctx.fill();
      //   }
      // }
    });
  }

  requestAnimationFrame(renderPrediction);
}

async function main() {
  await tf.setBackend(state.backend);

  await setupCamera();
  video.play();
  videoWidth = video.videoWidth;
  videoHeight = video.videoHeight;
  video.width = videoWidth;
  video.height = videoHeight;

  canvas = document.getElementById("output");
  canvas.width = videoWidth;
  canvas.height = videoHeight;
  const canvasContainer = document.querySelector(".canvas-wrapper");
  canvasContainer.style = `width: ${videoWidth}px; height: ${videoHeight}px`;

  ctx = canvas.getContext("2d");

  ctx.fillStyle = "#32EEDB";
  ctx.strokeStyle = "#32EEDB";
  ctx.lineWidth = 0.5;

  model = await facemesh.load({ maxFaces: state.maxFaces });
  renderPrediction();
}

main();
